import React, { useState, useEffect, useRef } from "react";
import { message, Modal } from "antd";
import { useTender } from "../../../contexts/TenderContext";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import MainLayout from "../../layout/MainLayout";
import StandardDrainageView from "./drainage/StandardDrainageView";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { useNavigate } from "react-router-dom";
import {
  DownloadOutlined,
  SaveOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { COLORS } from "../../../utils/constants";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";

interface DrainageRow {
  key: string;
  plotNo?: string; // Optional - kept for backward compatibility and import filtering
  housetype?: string; // Optional - kept for backward compatibility
  // Manual Entry fields
  plots: number;
  apartments: number;
  nmePhic: number;
  yardGully: number;
  drainageChannel: number;
  landDrain: number;
  cellularAtten: number;
  // Polypipe sections
  polypipeFoul: number;
  polypipeStorm: number;
  // Osma sections
  osmaFoul: number;
  osmaStorm: number;
  // Additional fields for OSMA-FOUL
  gravel: number;
  concealedSetSurround: number;
  tipOnSite: number;
  osmaNmePpic: number;
  osma600mmNmePpic: number;
  // Additional fields for OSMA-STORM
  osmaStormGravel: number;
  osmaStormConcreteBedSurround: number;
  osmaStormTipOnsite: number;
  osmaStormNmePpic: number;
  osmaStorm600mmNmePpic: number;
  osmaStormYardGully: number;
  osmaStormDrainageChannel: number;
  osmaStormLandDrain: number;
  // Additional fields for POLYPIPE-FOUL
  polypipeGravel: number;
  polypipeConcreteBedSurround: number;
  polypipeTipOnSite: number;
  polypipeNmePpic: number;
  polypipe600mmNmePpic: number;
  // Additional fields for POLYPIPE-STORM
  polypipeStormGravel: number;
  polypipeStormConcreteBedSurround: number;
  polypipeStormTipOnSite: number;
  polypipeStormNmePpic: number;
  polypipeStorm600mmNmePpic: number;
  polypipeStormYardGully: number;
  polypipeStormDrainageChannel: number;
  polypipeStormLandDrain: number;
  // SW & S detailed fields for OSMA FOUL
  osma32mmBend: number;
  osmaPPIC: number;
  osma50mmBend: number;
  osma68mmBend: number;
  osma82mmBend: number;
  osma110mmBend: number;
  osma32mmPipe: number;
  osma40mmPipe: number;
  osma50mmPipe: number;
  osma68mmPipe: number;
  osma82mmPipe: number;
  osma110mmPipe: number;
  // SW & S detailed fields for OSMA STORM
  osmaStorm32mmBend: number;
  osmaStorPPIC: number;
  osmaStorm50mmBend: number;
  osmaStorm68mmBend: number;
  osmaStorm100100JunctionTripleSocket: number;
  osmaStorm82mmBend: number;
  osmaStorm110mmBend: number;
  osmaStorm32mmPipe: number;
  osmaStorm40mmPipe: number;
  osmaStorm50mmPipe: number;
  osmaStorm68mmPipe: number;
  osmaStorm82mmPipe: number;
  osmaStorm110mmPipe: number;
  // SW & S detailed fields for POLYPIPE FOUL
  polypipe32mmBend: number;
  polypipePPIC: number;
  polypipe50mmBend: number;
  polypipe68mmBend: number;
  polypipe82mmBend: number; // Concrete to rest bends
  polypipe110mmBend: number;
  polypipe32mmPipe: number;
  polypipe40mmPipe: number;
  polypipe50mmPipe: number;
  polypipe68mmPipe: number;
  polypipe82mmPipe: number;
  polypipe110mmPipe: number;
  // SW & S detailed fields for POLYPIPE STORM
  polypipeStorm32mmBend: number;
  polypipeStormPPIC: number;
  polypipeStorm50mmBend: number;
  polypipeStorm68mmBend: number;
  polypipeStorm100100JunctionTripleSocket: number;
  polypipeStorm82mmBend: number;
  polypipeStorm110mmBend: number;
  polypipeStorm32mmPipe: number;
  polypipeStorm40mmPipe: number;
  polypipeStorm50mmPipe: number;
  polypipeStorm68mmPipe: number;
  polypipeStorm82mmPipe: number;
  polypipeStorm110mmPipe: number;
}

const excelColumns: ExcelColumn[] = [
  // plotNo and housetype removed as per user request
  { key: "plots", title: "Plots" },
  { key: "apartments", title: "Apartments" },
  { key: "nmePhic", title: "NME PHIC" },
  { key: "yardGully", title: "Yard Gully" },
  { key: "drainageChannel", title: "Drainage Channel" },
  { key: "landDrain", title: "Land Drain" },
  { key: "cellularAtten", title: "Cellular Atten" },
  { key: "polypipeFoul", title: "Polypipe Foul" },
  { key: "polypipeStorm", title: "Polypipe Storm" },
  { key: "osmaFoul", title: "Osma Foul" },
  { key: "osmaStorm", title: "Osma Storm" },
  // OSMA FOUL columns
  { key: "osma32mmBend", title: "OSMA FOUL - Lab per plot (Nr)" },
  { key: "osmaPPIC", title: "OSMA FOUL - PPIC's (Nr)" },
  { key: "osma50mmBend", title: "OSMA FOUL - Mini PPIC's (Nr)" },
  { key: "osma68mmBend", title: "OSMA FOUL - SVP (Nr)" },
  { key: "osma82mmBend", title: "OSMA FOUL - Concrete to rest bends (Nr)" },
  { key: "osma110mmBend", title: "OSMA FOUL - 100/100 junctions (Nr)" },
  { key: "osma32mmPipe", title: "OSMA FOUL - 100mm pipe (Nr)" },
  { key: "osma40mmPipe", title: "OSMA FOUL - 100mm couplers (Nr)" },
  { key: "osma50mmPipe", title: "OSMA FOUL - 100/150 junction (m)" },
  { key: "osma68mmPipe", title: "OSMA FOUL - 150/150 junction (Nr)" },
  { key: "osma82mmPipe", title: "OSMA FOUL - 150mm dia pipe (m)" },
  { key: "osma110mmPipe", title: "OSMA FOUL - 150mm couplers (m)" },
  { key: "gravel", title: "OSMA FOUL - Gravel (m³)" },
  {
    key: "concealedSetSurround",
    title: "OSMA FOUL - Concrete bed & surround (m³)",
  },
  { key: "tipOnSite", title: "OSMA FOUL - Tip onsite (m³)" },
  { key: "osmaNmePpic", title: "OSMA FOUL - NME PPIC (Nr)" },
  { key: "osma600mmNmePpic", title: "600mm NME PPIC (Nr)" },
  // OSMA STORM columns
  { key: "osmaStorm32mmBend", title: "OSMA STORM - Lab per plot (Nr)" },
  { key: "osmaStorPPIC", title: "OSMA STORM - PPIC's (Nr)" },
  { key: "osmaStorm50mmBend", title: "OSMA STORM - Rodding Eye (Nr)" },
  { key: "osmaStorm68mmBend", title: "OSMA STORM - RWP (m)" },
  {
    key: "osmaStorm100100JunctionTripleSocket",
    title: "OSMA STORM - 100/100 Junction (Triple Socket) (Nr)",
  },
  { key: "osmaStorm82mmBend", title: "OSMA STORM - 100mm pipe (Nr)" },
  { key: "osmaStorm110mmBend", title: "OSMA STORM - 100mm couplers (Nr)" },
  { key: "osmaStorm32mmPipe", title: "OSMA STORM - 100/150 junction (Nr)" },
  { key: "osmaStorm40mmPipe", title: "OSMA STORM - 150/150 junction (m)" },
  { key: "osmaStorm50mmPipe", title: "OSMA STORM - 150mm dia pipe (Nr)" },
  { key: "osmaStorm68mmPipe", title: "OSMA STORM - 150mm couplers (m)" },
  { key: "osmaStormGravel", title: "OSMA STORM - Gravel (m)" },
  {
    key: "osmaStormConcreteBedSurround",
    title: "OSMA STORM - Concrete bed & surround (m³)",
  },
  {
    key: "osmaStormTipOnsite",
    title: "OSMA STORM - Tip onsite (m³)",
  },
  { key: "osmaStormNmePpic", title: "OSMA STORM - NME PPIC (Nr)" },
  { key: "osmaStorm600mmNmePpic", title: "600mm NME PPIC (Nr)" },
  { key: "osmaStormYardGully", title: "OSMA STORM - Yard Gully (Nr)" },
  { key: "osmaStormDrainageChannel", title: "OSMA STORM - Drainage Channel (m)" },
  { key: "osmaStormLandDrain", title: "OSMA STORM - Land Drain (m)" },
  // POLYPIPE FOUL columns
  { key: "polypipe32mmBend", title: "POLYPIPE FOUL - Lab per plot (Nr)" },
  { key: "polypipePPIC", title: "POLYPIPE FOUL - PPIC's (Nr)" },
  { key: "polypipe50mmBend", title: "POLYPIPE FOUL - Mini PPIC's (Nr)" },
  { key: "polypipe68mmBend", title: "POLYPIPE FOUL - SVP (Nr)" },
  {
    key: "polypipe82mmBend",
    title: "POLYPIPE FOUL - Concrete to rest bends (Nr)",
  },
  { key: "polypipe110mmBend", title: "POLYPIPE FOUL - 100/100 Junctions (Nr)" },
  { key: "polypipe32mmPipe", title: "POLYPIPE FOUL - 100mm pipe (m)" },
  { key: "polypipe40mmPipe", title: "POLYPIPE FOUL - 100mm couplers (Nr)" },
  { key: "polypipe50mmPipe", title: "POLYPIPE FOUL - 100/150 junction (Nr)" },
  { key: "polypipe68mmPipe", title: "POLYPIPE FOUL - 150/150 junction (Nr)" },
  { key: "polypipe82mmPipe", title: "POLYPIPE FOUL - 150mm dia pipe (m)" },
  { key: "polypipe110mmPipe", title: "POLYPIPE FOUL - 150mm couplers (Nr)" },
  { key: "polypipeGravel", title: "POLYPIPE FOUL - Gravel (m)" },
  {
    key: "polypipeConcreteBedSurround",
    title: "POLYPIPE FOUL - Concrete bed & surround (m³)",
  },
  { key: "polypipeTipOnSite", title: "POLYPIPE FOUL - Tip onsite (m³)" },
  { key: "polypipeNmePpic", title: "POLYPIPE FOUL - NME PPIC (Nr)" },
  { key: "polypipe600mmNmePpic", title: "600mm NME PPIC (Nr)" },
  // POLYPIPE STORM columns
  { key: "polypipeStorm32mmBend", title: "POLYPIPE STORM" },
  { key: "polypipeStormPPIC", title: "POLYPIPE STORM - PPIC's (Nr)" },
  { key: "polypipeStorm50mmBend", title: "POLYPIPE STORM - Rodding Eye (Nr)" },
  { key: "polypipeStorm68mmBend", title: "POLYPIPE STORM - RWP (m)" },
  { key: "polypipeStorm100100JunctionTripleSocket", title: "POLYPIPE STORM - 100/100 Junction (Triple Socket) (Nr)" },
  { key: "polypipeStorm82mmBend", title: "POLYPIPE STORM - 100mm pipe (Nr)" },
  {
    key: "polypipeStorm110mmBend",
    title: "POLYPIPE STORM - 100mm couplers (Nr)",
  },
  {
    key: "polypipeStorm32mmPipe",
    title: "POLYPIPE STORM - 100/150 junction (Nr)",
  },
  {
    key: "polypipeStorm40mmPipe",
    title: "POLYPIPE STORM - 150/150 junction (m)",
  },
  {
    key: "polypipeStorm50mmPipe",
    title: "POLYPIPE STORM - 150mm dia pipe (Nr)",
  },
  {
    key: "polypipeStorm68mmPipe",
    title: "POLYPIPE STORM - 150mm couplers (m)",
  },
  { key: "polypipeStormGravel", title: "POLYPIPE STORM - Gravel (m)" },
  {
    key: "polypipeStormConcreteBedSurround",
    title: "POLYPIPE STORM - Concrete bed & surround (m³)",
  },
  { key: "polypipeStormTipOnSite", title: "POLYPIPE STORM - Tip onsite (m³)" },
  { key: "polypipeStormNmePpic", title: "POLYPIPE STORM - NME PPIC (Nr)" },
  { key: "polypipeStorm600mmNmePpic", title: "600mm NME PPIC (Nr)" },
  { key: "polypipeStormYardGully", title: "POLYPIPE STORM - Yard Gully (Nr)" },
  { key: "polypipeStormDrainageChannel", title: "POLYPIPE STORM - Drainage Channel (m)" },
  { key: "polypipeStormLandDrain", title: "POLYPIPE STORM - Land Drain (m)" },
];

const Drainage: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent =
    tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState(
    "groundworks_includePreliminariesDisplay",
    preliminariesPercent,
  );
  const includeOhp = getCheckboxState(
    "groundworks_includeOhpDisplay",
    ohpPercent,
  );
  const includeDiscount = getCheckboxState(
    "groundworks_includeDiscountDisplay",
    discountPercent,
  );

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  // Only Standard View data state
  const [standardData, setStandardData] = useState<DrainageRow[]>([]);
  const [supplierType, setSupplierType] = useState<string>("Polypipe");
  const [standardSupplier, setStandardSupplier] = useState<"Osma" | "Polypipe">(
    "Osma",
  );
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const hasDetectedSupplier = useRef(false);
  const [drainageColumnHeaders, setDrainageColumnHeaders] = useState<Record<string, string>>(
    tender?.groundworks?.drainageColumnHeaders || {}
  );
  const [drainageDimensionValues, setDrainageDimensionValues] = useState<Record<string, string>>(
    tender?.groundworks?.drainageDimensionValues || {}
  );
  const [drainagePolypipeConfig, setDrainagePolypipeConfig] = useState<Record<string, string>>(
    (tender?.groundworks as any)?.drainagePolypipeConfig || {}
  );

  // Use standard data
  const data = standardData;
  const setData = setStandardData;

  // Helper function to check if table has any non-zero values
  const hasTableValues = (data: DrainageRow[]): boolean => {
    if (!data || data.length === 0) return false;

    return data.some((row) => {
      // Check all numeric fields except key, plotNo, housetype
      const fields = Object.keys(row).filter(
        (key) => !["key", "plotNo", "housetype"].includes(key),
      );

      return fields.some((field) => {
        const value = row[field as keyof DrainageRow];
        return typeof value === "number" && value !== 0;
      });
    });
  };

  // Helper function to detect which supplier has data
  const detectSupplierFromData = (
    data: DrainageRow[],
  ): "Osma" | "Polypipe" | null => {
    if (!data || data.length === 0) return null;

    // Check for OSMA fields with values
    const hasOsmaValues = data.some((row) => {
      return (
        row.osma32mmBend !== 0 ||
        row.osmaPPIC !== 0 ||
        row.osma50mmBend !== 0 ||
        row.osma68mmBend !== 0 ||
        row.osma82mmBend !== 0 ||
        row.osma110mmBend !== 0 ||
        row.osma32mmPipe !== 0 ||
        row.osma40mmPipe !== 0 ||
        row.osma50mmPipe !== 0 ||
        row.osma68mmPipe !== 0 ||
        row.osma82mmPipe !== 0 ||
        row.osma110mmPipe !== 0 ||
        row.osmaStorm32mmBend !== 0 ||
        row.osmaStorPPIC !== 0 ||
        row.osmaStorm50mmBend !== 0 ||
        row.osmaStorm68mmBend !== 0 ||
        row.osmaStorm82mmBend !== 0 ||
        row.osmaStorm110mmBend !== 0 ||
        row.osmaStorm32mmPipe !== 0 ||
        row.osmaStorm40mmPipe !== 0 ||
        row.osmaStorm50mmPipe !== 0 ||
        row.osmaStorm68mmPipe !== 0 ||
        row.gravel !== 0 ||
        row.concealedSetSurround !== 0 ||
        row.tipOnSite !== 0 ||
        row.osmaStormGravel !== 0 ||
        row.osmaStormConcreteBedSurround !== 0 ||
        row.osmaStormTipOnsite !== 0
      );
    });

    // Check for POLYPIPE fields with values
    const hasPolypipeValues = data.some((row) => {
      return (
        row.polypipe32mmBend !== 0 ||
        row.polypipePPIC !== 0 ||
        row.polypipe50mmBend !== 0 ||
        row.polypipe68mmBend !== 0 ||
        row.polypipe82mmBend !== 0 ||
        row.polypipe110mmBend !== 0 ||
        row.polypipe32mmPipe !== 0 ||
        row.polypipe40mmPipe !== 0 ||
        row.polypipe50mmPipe !== 0 ||
        row.polypipe68mmPipe !== 0 ||
        row.polypipe82mmPipe !== 0 ||
        row.polypipe110mmPipe !== 0 ||
        row.polypipeStorm32mmBend !== 0 ||
        row.polypipeStormPPIC !== 0 ||
        row.polypipeStorm50mmBend !== 0 ||
        row.polypipeStorm68mmBend !== 0 ||
        row.polypipeStorm82mmBend !== 0 ||
        row.polypipeStorm110mmBend !== 0 ||
        row.polypipeStorm32mmPipe !== 0 ||
        row.polypipeStorm40mmPipe !== 0 ||
        row.polypipeStorm50mmPipe !== 0 ||
        row.polypipeStorm68mmPipe !== 0 ||
        row.polypipeGravel !== 0 ||
        row.polypipeConcreteBedSurround !== 0 ||
        row.polypipeTipOnSite !== 0 ||
        row.polypipeStormGravel !== 0 ||
        row.polypipeStormConcreteBedSurround !== 0 ||
        row.polypipeStormTipOnSite !== 0
      );
    });

    // Return the detected supplier
    if (hasOsmaValues && !hasPolypipeValues) return "Osma";
    if (hasPolypipeValues && !hasOsmaValues) return "Polypipe";

    return null; // Either both have values or neither has values
  };

  // Helper function to clear only the specified supplier's table values
  const clearSupplierTableValues = (
    supplierToClear: "Osma" | "Polypipe",
  ): DrainageRow[] => {
    return data.map((row) => {
      if (supplierToClear === "Osma") {
        // Clear only OSMA fields, keep POLYPIPE and other fields
        return {
          ...row,
          osmaFoul: 0,
          osmaStorm: 0,
          gravel: 0,
          concealedSetSurround: 0,
          tipOnSite: 0,
          osmaStormGravel: 0,
          osmaStormConcreteBedSurround: 0,
          osmaStormTipOnsite: 0,
          osma32mmBend: 0,
          osmaPPIC: 0,
          osma50mmBend: 0,
          osma68mmBend: 0,
          osma82mmBend: 0,
          osma110mmBend: 0,
          osma32mmPipe: 0,
          osma40mmPipe: 0,
          osma50mmPipe: 0,
          osma68mmPipe: 0,
          osma82mmPipe: 0,
          osma110mmPipe: 0,
          osmaStorm32mmBend: 0,
          osmaStorPPIC: 0,
          osmaStorm50mmBend: 0,
          osmaStorm68mmBend: 0,
          osmaStorm100100JunctionTripleSocket: 0,
          osmaStorm82mmBend: 0,
          osmaStorm110mmBend: 0,
          osmaStorm32mmPipe: 0,
          osmaStorm40mmPipe: 0,
          osmaStorm50mmPipe: 0,
          osmaStorm68mmPipe: 0,
          osmaStorm82mmPipe: 0,
          osmaStorm110mmPipe: 0,
          osmaNmePpic: 0,
          osma600mmNmePpic: 0,
          osmaStormNmePpic: 0,
          osmaStorm600mmNmePpic: 0,
          osmaStormYardGully: 0,
          osmaStormDrainageChannel: 0,
          osmaStormLandDrain: 0,
        };
      } else {
        // Clear only POLYPIPE fields, keep OSMA and other fields
        return {
          ...row,
          polypipeFoul: 0,
          polypipeStorm: 0,
          polypipeGravel: 0,
          polypipeConcreteBedSurround: 0,
          polypipeTipOnSite: 0,
          polypipeStormGravel: 0,
          polypipeStormConcreteBedSurround: 0,
          polypipeStormTipOnSite: 0,
          polypipe32mmBend: 0,
          polypipePPIC: 0,
          polypipe50mmBend: 0,
          polypipe68mmBend: 0,
          polypipe82mmBend: 0, // Concrete to rest bends - FOUL
          polypipe110mmBend: 0,
          polypipe32mmPipe: 0,
          polypipe40mmPipe: 0,
          polypipe50mmPipe: 0,
          polypipe68mmPipe: 0,
          polypipe82mmPipe: 0,
          polypipe110mmPipe: 0,
          polypipeStorm32mmBend: 0,
          polypipeStormPPIC: 0,
          polypipeStorm50mmBend: 0,
          polypipeStorm68mmBend: 0,
          polypipeStorm82mmBend: 0,
          polypipeStorm110mmBend: 0,
          polypipeStorm32mmPipe: 0,
          polypipeStorm40mmPipe: 0,
          polypipeStorm50mmPipe: 0,
          polypipeStorm68mmPipe: 0,
          polypipeStorm82mmPipe: 0,
          polypipeStorm110mmPipe: 0,
          polypipeNmePpic: 0,
          polypipe600mmNmePpic: 0,
          polypipeStorm100100JunctionTripleSocket: 0,
          polypipeStormNmePpic: 0,
          polypipeStorm600mmNmePpic: 0,
          polypipeStormYardGully: 0,
          polypipeStormDrainageChannel: 0,
          polypipeStormLandDrain: 0,
        };
      }
    });
  };

  // Handler for supplier change with confirmation
  const handleSupplierChange = (newSupplier: "Osma" | "Polypipe") => {
    // If same supplier, do nothing
    if (newSupplier === standardSupplier) return;

    // Check if table has any values
    if (hasTableValues(data)) {
      Modal.confirm({
        title: "Change Supplier Type",
        content:
          "It should be either OSMA or POLYPIPE. This Selection will clear current table values.",
        okText: "Continue",
        cancelText: "Cancel",
        onOk: () => {
          // Clear only the current supplier's values (the one being changed FROM)
          const clearedData = clearSupplierTableValues(standardSupplier);
          setStandardData(clearedData);
          // Reset configure modal values for both suppliers so the new supplier
          // starts with empty/0 configuration.
          setDrainageDimensionValues({});
          setDrainagePolypipeConfig({});
          updateField("groundworks", {
            ...tender.groundworks,
            drainageTableStandard: clearedData,
            drainageDimensionValues: {},
            drainagePolypipeConfig: {},
          });
          setStandardSupplier(newSupplier);
          message.success(
            `Supplier changed to ${newSupplier}. ${standardSupplier} table values cleared.`,
          );
        },
        onCancel: () => {
          // Do nothing - keep current supplier
        },
      });
    } else {
      // No values in table — reset configure modal values and change supplier
      setDrainageDimensionValues({});
      setDrainagePolypipeConfig({});
      updateField("groundworks", {
        ...tender.groundworks,
        drainageDimensionValues: {},
        drainagePolypipeConfig: {},
      });
      setStandardSupplier(newSupplier);
    }
  };

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<DrainageRow[]>({
    pageId: "drainage",
    onChangeDetected: (changes) => {
    },
  }); */

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel: baseHandleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<DrainageRow>({
    filename: "Drainage_Standard_View",
    sheetName: "Drainage",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => ({
        ...createEmptyRow(`imported_${Date.now()}_${index}`),
        ...row,
      }));

      // Update standard data
      setStandardData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        drainageTableStandard: processedData,
      });
    },
    skipValidation: true,
  });

  // Export handler with view-specific filename
  const handleExportExcel = () => {
    baseHandleExportExcel();
  };

  // Custom import handler - simplified approach
  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
            type: "array",
          });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          // Read all rows as arrays - keep all columns including empty ones
          const allRows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            defval: "", // Keep empty cells
            blankrows: false,
          }) as any[][];

          // The actual column headers are in row 2
          const headerRow = allRows[2] || [];
          const unitRow = allRows[3] || [];

          // Extracted dynamic headers (populated during column detection)
          const extractedHeaders: Record<string, string> = {};
          const extractedDimValues: Record<string, string> = {};

          // Print all available headers
          for (let i = 0; i < headerRow.length; i++) {
            const header = String(headerRow[i] || "").trim();
            const unit = String(unitRow[i] || "").trim();
            if (header || unit) {
            }
          }

          // Helper function to find column index by matching header text
          const findColumn = (searchTerm: string): number => {
            const searchLower = searchTerm.toLowerCase().trim();

            for (let i = 0; i < headerRow.length; i++) {
              const header = String(headerRow[i] || "")
                .toLowerCase()
                .trim();

              // Debug each comparison for first 30 columns or if there's a header
              if (i < 30 || header) {
              }

              if (header.includes(searchLower)) {
                return i;
              }
            }
            return -1;
          };

          // Helper to find all occurrences of a search term
          const findAllColumns = (searchTerm: string): number[] => {
            const searchLower = searchTerm.toLowerCase().trim();
            const results: number[] = [];
            for (let i = 0; i < headerRow.length; i++) {
              const header = String(headerRow[i] || "")
                .toLowerCase()
                .trim();
              if (header.includes(searchLower)) {
                results.push(i);
              }
            }
            return results;
          };

          // Build column mapping by matching headers in order

          // Find all occurrences of each header type based on the active view
          let columnMap: any = {};

          if (false) {
            // SW & S View - Search for construction/excavation column headers
            const exc100mmCols = findAllColumns("exc for & lay 100mm pipe");

            // Try multiple search patterns for 150mm pipe
            let exc150mmCols = findAllColumns("exc for & lay 150mm pipe");
            if (exc150mmCols.length === 0) {
              exc150mmCols = findAllColumns("lay 150mm pipe");
            }
            if (exc150mmCols.length === 0) {
              exc150mmCols = findAllColumns("150mm pipe, gravel");
            }

            const exc225mmCols = findAllColumns("exc for & lay 225mm pipe");
            const construct250mmCols = findAllColumns(
              "construct 250mm dia inspection",
            );
            const construct450mmLess1200Cols = findAllColumns(
              "construct 450mm dia inspection chambers <1200mm",
            );
            const construct450mmGreater1200Cols = findAllColumns(
              "construct 450mm dia inspection chambers >1200mm",
            );
            const construct600mmCols = findAllColumns(
              "construct 600mm dia inspection",
            );
            const svpConnectionsCols = findAllColumns("svp connection");
            const demarcationChamberCols = findAllColumns(
              "demarcation chamber",
            );
            // STORM specific columns
            const rwpConnectionsCols = findAllColumns("rwp conncetion"); // Note typo in header
            const roddingEyesCols = findAllColumns("rodding eye");
            const yardGulliesCols = findAllColumns("yard gull");
            const accoChannelCols = findAllColumns("acco channel");
            const landDrainCols = findAllColumns("100mm land drain");

            columnMap = {
              plotNo: 0,
              housetype: 1,
              // OSMA FOUL section (first occurrence)
              osmaExc100mm: exc100mmCols[0] || -1, // Exc for & lay 100mm pipe
              osmaExc150mm: exc150mmCols[0] || -1, // Exc for & lay 150mm pipe
              osma50mmBend: construct250mmCols[0] || -1, // Construct 250mm dia inspection chambers
              osma68mmBend: construct450mmLess1200Cols[0] || -1, // Construct 450mm dia <1200mm
              osma82mmBend: construct450mmGreater1200Cols[0] || -1, // Construct 450mm dia >1200mm
              osma110mmBend: svpConnectionsCols[0] || -1, // SVP connections
              osma32mmPipe: demarcationChamberCols[0] || -1, // Demarcation chamber
              osma32mmBend: -1, // Not in SW&S View
              osma40mmBend: -1, // Not in SW&S View
              osma40mmPipe: -1, // Not in FOUL
              osma50mmPipe: -1, // Not in FOUL
              osma68mmPipe: -1, // Not in FOUL
              osma82mmPipe: -1, // Not in FOUL
              osma110mmPipe: -1, // Not in FOUL
              gravel: -1, // Not mapped
              concealedSetSurround: -1, // Not mapped
              tipOnSite: -1, // Not mapped
              // OSMA STORM section (second occurrence)
              osmaStormExc100mm: exc100mmCols[1] || -1, // Exc for & lay 100mm pipe
              osmaStormExc150mm: exc150mmCols[1] || -1, // Exc for & lay 150mm pipe
              osmaStormExc225mm: exc225mmCols[0] || -1, // Exc for & lay 225mm pipe
              osmaStorm68mmBend: construct450mmLess1200Cols[1] || -1, // Construct 450mm dia <1200mm
              osmaStorm82mmBend: construct450mmGreater1200Cols[1] || -1, // Construct 450mm dia >1200mm
              osmaStorm110mmBend: construct600mmCols[0] || -1, // Construct 600mm dia >1200mm
              osmaStorm32mmPipe: rwpConnectionsCols[0] || -1, // RWP Connections
              osmaStorm40mmPipe: roddingEyesCols[0] || -1, // Rodding Eyes
              osmaStorm50mmPipe: yardGulliesCols[0] || -1, // Yard Gullies
              osmaStorm68mmPipe: accoChannelCols[0] || -1, // Acco Channel
              osmaStorm82mmPipe: landDrainCols[0] || -1, // Exc for & lay 100mm land drain
              osmaStorm110mmPipe: demarcationChamberCols[1] || -1, // Demarcation chamber
              osmaStorm32mmBend: -1, // Not in SW&S View
              osmaStorm40mmBend: -1, // Not in SW&S View
              osmaStorm50mmBend: -1, // Not in SW&S View
              osmaStorm100100JunctionTripleSocket: -1, // Not mapped
              // POLYPIPE FOUL section (third occurrence)
              polypipeExc100mm: exc100mmCols[2] || -1, // Exc for & lay 100mm pipe
              polypipeExc150mm: exc150mmCols[2] || -1, // Exc for & lay 150mm pipe
              polypipe50mmBend: construct250mmCols[1] || -1, // Construct 250mm dia inspection chambers
              polypipe68mmBend: construct450mmLess1200Cols[2] || -1, // Construct 450mm dia <1200mm
              polypipe82mmBend: construct450mmGreater1200Cols[2] || -1, // Construct 450mm dia >1200mm
              polypipe110mmBend: svpConnectionsCols[1] || -1, // SVP connections
              polypipe32mmPipe: demarcationChamberCols[2] || -1, // Demarcation chamber
              polypipe32mmBend: -1, // Not in SW&S View
              polypipe40mmBend: -1, // Not in SW&S View
              polypipe40mmPipe: -1, // Not in FOUL
              polypipe50mmPipe: -1, // Not in FOUL
              polypipe68mmPipe: -1, // Not in FOUL
              polypipe82mmPipe: -1, // Not in FOUL
              polypipe110mmPipe: -1, // Not in FOUL
              // POLYPIPE STORM section (fourth occurrence)
              polypipeStormExc100mm: exc100mmCols[3] || -1, // Exc for & lay 100mm pipe
              polypipeStormExc150mm: exc150mmCols[3] || -1, // Exc for & lay 150mm pipe
              polypipeStormExc225mm: exc225mmCols[1] || -1, // Exc for & lay 225mm pipe
              polypipeStorm68mmBend: construct450mmLess1200Cols[3] || -1, // Construct 450mm dia <1200mm
              polypipeStorm82mmBend: construct450mmGreater1200Cols[3] || -1, // Construct 450mm dia >1200mm
              polypipeStorm110mmBend: construct600mmCols[1] || -1, // Construct 600mm dia >1200mm
              polypipeStorm32mmPipe: rwpConnectionsCols[1] || -1, // RWP Connections
              polypipeStorm40mmPipe: roddingEyesCols[1] || -1, // Rodding Eyes
              polypipeStorm50mmPipe: yardGulliesCols[1] || -1, // Yard Gullies
              polypipeStorm68mmPipe: accoChannelCols[1] || -1, // Acco Channel
              polypipeStorm82mmPipe: landDrainCols[1] || -1, // Exc for & lay 100mm land drain
              polypipeStorm110mmPipe: demarcationChamberCols[3] || -1, // Demarcation chamber
              polypipeStorm32mmBend: -1, // Not in SW&S View
              polypipeStorm40mmBend: -1, // Not in SW&S View
              polypipeStorm50mmBend: -1, // Not in SW&S View
            };
          } else {
            // Standard View - Search for standard column headers
            const labPerPlotCols = findAllColumns("lab per plot");
            const miniPpicCols = findAllColumns("mini ppic");
            // Find PPIC columns but exclude Mini PPIC and NME PPIC
            const allPpicCols = findAllColumns("ppic");
            const ppicCols = allPpicCols.filter((col) => {
              const header = String(headerRow[col] || "")
                .toLowerCase()
                .trim();
              return !header.includes("mini") && !header.includes("nme");
            });
            const nmePpicCols = allPpicCols.filter((col) => {
              const header = String(headerRow[col] || "")
                .toLowerCase()
                .trim();
              return header.includes("nme") && !header.includes("600");
            });
            const nmePpic600Cols = allPpicCols.filter((col) => {
              const header = String(headerRow[col] || "")
                .toLowerCase()
                .trim();
              return header.includes("nme") && header.includes("600");
            });
            const svpCols = findAllColumns("svp");
            // Try multiple variations to find "Concrete to rest bends"
            let concreteRestCols = findAllColumns("concrete to rest bends");
            if (concreteRestCols.length === 0) {
              concreteRestCols = findAllColumns("concrete to rest bend");
            }
            if (concreteRestCols.length === 0) {
              concreteRestCols = findAllColumns("concrete to rest");
            }
            if (concreteRestCols.length === 0) {
              concreteRestCols = findAllColumns("concrtete to rest");
            }
            if (concreteRestCols.length === 0) {
              concreteRestCols = findAllColumns("concrete rest");
            }
            const junction100Cols = findAllColumns("100/100 junction").filter(c => {
              const header = String(headerRow[c] || "").toLowerCase();
              return !header.includes("triple socket") && !header.includes("triple)");
            });
            const pipe100Cols = findAllColumns("100mm pipe");
            const coupler100Cols = findAllColumns("100mm coupler");
            const junction100_150Cols = findAllColumns("100/150");
            const junction150_150Cols = findAllColumns("150/150");
            const pipe150Cols = findAllColumns("150mm dia pipe");
            const coupler150Cols = findAllColumns("150mm coupler");
            const gravelCols = findAllColumns("gravel");
            const concreteBedCols = findAllColumns("concrete bed & surround");
            const tipOnsiteCols = findAllColumns("tip onsite");
            const roddingEyeCols = findAllColumns("rodding eye");
            const rwpCols = findAllColumns("rwp");
            // Search for "triple socket" or "junction (triple" to catch "100/100 Junction (Triple Socket)"
            let tripleSocketCols = findAllColumns("triple socket");
            if (tripleSocketCols.length === 0) {
              tripleSocketCols = findAllColumns("junction (triple");
            }
            // Filter to supplier sections only (col > 12) to avoid matching manual entry cols at 0-12
            const yardGullyCols = findAllColumns("yard gully").filter(c => c > 12);
            const drainageChannelCols = findAllColumns("drainage channel").filter(c => c > 12);
            // Find land drain columns - search for all possible header variations and combine
            const landDrainSearchTerms = ["land drain", "land drainage", "silt trap", "connection to drainage"];
            let landDrainCols: number[] = [];
            for (const term of landDrainSearchTerms) {
              const found = findAllColumns(term).filter((c) => c > 12 && !landDrainCols.includes(c));
              landDrainCols.push(...found);
            }
            landDrainCols.sort((a, b) => a - b);

            columnMap = {
              plotNo: 0,
              housetype: 1,
              // OSMA FOUL section (first occurrence)
              osma32mmBend: labPerPlotCols[0] || -1,
              osmaPPIC: ppicCols[0] || -1,
              osma50mmBend: miniPpicCols[0] || -1,
              osma68mmBend: svpCols[0] || -1,
              osma82mmBend: concreteRestCols[0] || -1,
              osma110mmBend: junction100Cols[0] || -1,
              osma32mmPipe: pipe100Cols[0] || -1,
              osma40mmPipe: coupler100Cols[0] || -1,
              osma50mmPipe: junction100_150Cols[0] || -1,
              osma68mmPipe: junction150_150Cols[0] || -1,
              osma82mmPipe: pipe150Cols[0] || -1,
              osma110mmPipe: coupler150Cols[0] || -1,
              gravel: gravelCols[0] || -1,
              concealedSetSurround: concreteBedCols[0] || -1,
              tipOnSite: tipOnsiteCols[0] || -1,
              osmaNmePpic: nmePpicCols[0] || -1,
              osma600mmNmePpic: nmePpic600Cols[0] || -1,
              // OSMA STORM section (second occurrence)
              osmaStorm32mmBend: labPerPlotCols[1] || -1,
              osmaStorPPIC: ppicCols[1] || -1,
              osmaStorm50mmBend: roddingEyeCols[0] || -1,
              osmaStorm68mmBend: rwpCols[0] || -1,
              osmaStorm100100JunctionTripleSocket: tripleSocketCols[0] || -1,
              osmaStorm82mmBend: pipe100Cols[1] || -1,
              osmaStorm110mmBend: coupler100Cols[1] || -1,
              osmaStorm32mmPipe: junction100_150Cols[1] || -1,
              osmaStorm40mmPipe: junction150_150Cols[1] || -1,
              osmaStorm50mmPipe: pipe150Cols[1] || -1,
              osmaStorm68mmPipe: coupler150Cols[1] || -1,
              osmaStorm82mmPipe: -1, // Not used in Standard View
              osmaStorm110mmPipe: -1, // Not used in Standard View
              // Additional tracking for OSMA STORM
              _osmaStormGravel: gravelCols[1] || -1,
              _osmaStormConcrete: concreteBedCols[1] || -1,
              _osmaStormTip: tipOnsiteCols[1] || -1,
              osmaStormNmePpic: nmePpicCols[1] || -1,
              osmaStorm600mmNmePpic: nmePpic600Cols[1] || -1,
              osmaStormYardGully: yardGullyCols[0] || -1,
              osmaStormDrainageChannel: drainageChannelCols[0] || -1,
              osmaStormLandDrain: landDrainCols[0] || -1,
              // POLYPIPE FOUL section (third occurrence)
              polypipe32mmBend: labPerPlotCols[2] || -1,
              polypipePPIC: ppicCols[2] || -1,
              polypipe40mmBend: -1, // Not in DrainageRow interface
              polypipe50mmBend: miniPpicCols[1] || -1,
              polypipe68mmBend: svpCols[1] || -1,
              polypipe82mmBend: concreteRestCols[1] || -1,
              polypipe110mmBend: junction100Cols[2] || -1, // Fixed: should be [2] not [1]
              polypipe32mmPipe: pipe100Cols[2] || -1,
              polypipe40mmPipe: coupler100Cols[2] || -1,
              polypipe50mmPipe: junction100_150Cols[2] || -1,
              polypipe68mmPipe: junction150_150Cols[2] || -1,
              polypipe82mmPipe: pipe150Cols[2] || -1,
              polypipe110mmPipe: coupler150Cols[2] || -1,
              // Additional tracking for POLYPIPE FOUL
              _polypipeGravel: gravelCols[2] || -1,
              _polypipeConcrete: concreteBedCols[2] || -1,
              _polypipeTip: tipOnsiteCols[2] || -1,
              polypipeNmePpic: nmePpicCols[2] || -1,
              polypipe600mmNmePpic: nmePpic600Cols[2] || -1,
              // POLYPIPE STORM section (fourth occurrence)
              polypipeStorm32mmBend: labPerPlotCols[3] || -1,
              polypipeStormPPIC: ppicCols[3] || -1,
              polypipeStorm40mmBend: -1, // Not in DrainageRow interface
              polypipeStorm50mmBend: roddingEyeCols[1] || -1,
              polypipeStorm68mmBend: rwpCols[1] || -1,
              polypipeStorm100100JunctionTripleSocket: tripleSocketCols[1] || -1,
              polypipeStorm82mmBend: pipe100Cols[3] || -1,
              polypipeStorm110mmBend: coupler100Cols[3] || -1,
              polypipeStorm32mmPipe: junction100_150Cols[3] || -1,
              polypipeStorm40mmPipe: junction150_150Cols[3] || -1,
              polypipeStorm50mmPipe: pipe150Cols[3] || -1,
              polypipeStorm68mmPipe: coupler150Cols[3] || -1,
              polypipeStorm82mmPipe: -1, // Not used in Standard View
              polypipeStorm110mmPipe: -1, // Not used in Standard View
              // Additional tracking for POLYPIPE STORM
              _polypipeStormGravel: gravelCols[3] || -1,
              _polypipeStormConcrete: concreteBedCols[3] || -1,
              _polypipeStormTip: tipOnsiteCols[3] || -1,
              polypipeStormNmePpic: nmePpicCols[3] || -1,
              polypipeStorm600mmNmePpic: nmePpic600Cols[3] || -1,
              polypipeStormYardGully: yardGullyCols[1] || -1,
              polypipeStormDrainageChannel: drainageChannelCols[1] || -1,
              polypipeStormLandDrain: landDrainCols[1] || -1,
            };

            // Extract dynamic column headers for Drainage Channel and Land Drain
            const drainageChannelOpts = [
              { code: "GWALPDD059", label: "A15 Drainage channel" },
              { code: "GWALPDD060", label: "B125 Drainage channel" },
              { code: "GWALPDD020", label: "Drainage channel" },
            ];
            const landDrainOpts = [
              { code: "GWALPDD057", label: "Connection to drainage system" },
              { code: "L&P3", label: "Silt trap chamber" },
              { code: "GWALPDD056", label: "Land drainage" },
            ];

            const matchCol = (colIdx: number, options: { code: string; label: string }[], key: string) => {
              if (colIdx >= 0 && colIdx < headerRow.length) {
                const hv = String(headerRow[colIdx] || "").trim();
                for (const opt of options) {
                  if (hv.toLowerCase().includes(opt.label.toLowerCase())) {
                    extractedHeaders[key] = opt.label;
                    extractedDimValues[key] = opt.code;
                    break;
                  }
                }
              }
            };

            if (drainageChannelCols.length > 0) matchCol(drainageChannelCols[0], drainageChannelOpts, "osmaStormDrainageChannel");
            if (landDrainCols.length > 0) matchCol(landDrainCols[0], landDrainOpts, "osmaStormLandDrain");
            if (drainageChannelCols.length > 1) matchCol(drainageChannelCols[1], drainageChannelOpts, "polypipeStormDrainageChannel");
            if (landDrainCols.length > 1) matchCol(landDrainCols[1], landDrainOpts, "polypipeStormLandDrain");
          }

          // Extract data rows starting from index 6
          const dataRows = allRows.slice(6).filter((row) => {
            return (
              row &&
              row.length > 0 &&
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== "",
              )
            );
          });

          // Map each row to DrainageRow format
          const importedRows: DrainageRow[] = [];

          for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            const actualExcelRowIndex = i + 6; // Since we started from row 6

            const plotNo = String(row[columnMap.plotNo] || "").trim();
            const housetype = String(row[columnMap.housetype] || "").trim();

            // Skip if no plot number or if it's the "Polypipe / Osma" row
            if (
              !plotNo ||
              plotNo === "0" ||
              plotNo.toLowerCase().includes("polypipe") ||
              plotNo.toLowerCase().includes("osma")
            ) {
              continue;
            }

            // Helper function to safely get numeric value
            const getNum = (colIndex: number, fieldName?: string): number => {
              if (colIndex === -1) {
                if (fieldName) return 0;
              }
              const val = row[colIndex];
              const headerText = String(headerRow[colIndex] || "").trim();
              if (fieldName) {
              }
              if (val === "" || val === null || val === undefined) return 0;
              const num = Number(val);
              return isNaN(num) ? 0 : num;
            };

            // Log all values being imported

            const drainageRow: DrainageRow = {
              key: `imported_${Date.now()}_${i}`,
              plotNo: plotNo,
              housetype: housetype,
              plots: 0,
              apartments: 0,
              nmePhic: 0,
              yardGully: 0,
              drainageChannel: 0,
              landDrain: 0,
              cellularAtten: 0,
              polypipeFoul: 0,
              polypipeStorm: 0,
              osmaFoul: 0,
              osmaStorm: 0,
              // Standard View fields
              gravel: getNum(columnMap.gravel, "gravel"),
              concealedSetSurround: getNum(
                columnMap.concealedSetSurround,
                "concealedSetSurround",
              ),
              tipOnSite: getNum(columnMap.tipOnSite, "tipOnSite"),
              osmaNmePpic: getNum(columnMap.osmaNmePpic, "osmaNmePpic"),
              osma600mmNmePpic: getNum(columnMap.osma600mmNmePpic, "osma600mmNmePpic"),
              // OSMA STORM additional fields
              osmaStormGravel: getNum(
                columnMap._osmaStormGravel,
                "_osmaStormGravel",
              ),
              osmaStormConcreteBedSurround: getNum(
                columnMap._osmaStormConcrete,
                "_osmaStormConcrete",
              ),
              osmaStormTipOnsite: getNum(
                columnMap._osmaStormTip,
                "_osmaStormTip",
              ),
              osmaStormNmePpic: getNum(columnMap.osmaStormNmePpic, "osmaStormNmePpic"),
              osmaStorm600mmNmePpic: getNum(columnMap.osmaStorm600mmNmePpic, "osmaStorm600mmNmePpic"),
              osmaStormYardGully: getNum(columnMap.osmaStormYardGully, "osmaStormYardGully"),
              osmaStormDrainageChannel: getNum(columnMap.osmaStormDrainageChannel, "osmaStormDrainageChannel"),
              osmaStormLandDrain: getNum(columnMap.osmaStormLandDrain, "osmaStormLandDrain"),
              // POLYPIPE FOUL additional fields
              polypipeGravel: getNum(
                columnMap._polypipeGravel,
                "_polypipeGravel",
              ),
              polypipeConcreteBedSurround: getNum(
                columnMap._polypipeConcrete,
                "_polypipeConcrete",
              ),
              polypipeTipOnSite: getNum(columnMap._polypipeTip, "_polypipeTip"),
              polypipeNmePpic: getNum(columnMap.polypipeNmePpic, "polypipeNmePpic"),
              polypipe600mmNmePpic: getNum(columnMap.polypipe600mmNmePpic, "polypipe600mmNmePpic"),
              // POLYPIPE STORM additional fields
              polypipeStormGravel: getNum(
                columnMap._polypipeStormGravel,
                "_polypipeStormGravel",
              ),
              polypipeStormConcreteBedSurround: getNum(
                columnMap._polypipeStormConcrete,
                "_polypipeStormConcrete",
              ),
              polypipeStormTipOnSite: getNum(
                columnMap._polypipeStormTip,
                "_polypipeStormTip",
              ),
              polypipeStormNmePpic: getNum(columnMap.polypipeStormNmePpic, "polypipeStormNmePpic"),
              polypipeStorm600mmNmePpic: getNum(columnMap.polypipeStorm600mmNmePpic, "polypipeStorm600mmNmePpic"),
              polypipeStormYardGully: getNum(columnMap.polypipeStormYardGully, "polypipeStormYardGully"),
              polypipeStormDrainageChannel: getNum(columnMap.polypipeStormDrainageChannel, "polypipeStormDrainageChannel"),
              polypipeStormLandDrain: getNum(columnMap.polypipeStormLandDrain, "polypipeStormLandDrain"),
              // OSMA FOUL
              osma32mmBend: getNum(columnMap.osma32mmBend, "osma32mmBend"),
              osmaPPIC: getNum(columnMap.osmaPPIC, "osmaPPIC"),
              osma40mmBend: getNum(columnMap.osma40mmBend, "osma40mmBend"),
              osma50mmBend: getNum(columnMap.osma50mmBend, "osma50mmBend"),
              osma68mmBend: getNum(columnMap.osma68mmBend, "osma68mmBend"),
              osma82mmBend: getNum(columnMap.osma82mmBend, "osma82mmBend"),
              osma110mmBend: getNum(columnMap.osma110mmBend, "osma110mmBend"),
              osma32mmPipe: getNum(columnMap.osma32mmPipe, "osma32mmPipe"),
              osma40mmPipe: getNum(columnMap.osma40mmPipe, "osma40mmPipe"),
              osma50mmPipe: getNum(columnMap.osma50mmPipe, "osma50mmPipe"),
              osma68mmPipe: getNum(columnMap.osma68mmPipe, "osma68mmPipe"),
              osma82mmPipe: getNum(columnMap.osma82mmPipe, "osma82mmPipe"),
              osma110mmPipe: getNum(columnMap.osma110mmPipe, "osma110mmPipe"),
              // OSMA FOUL Excavation fields
              osmaExc100mm: getNum(columnMap.osmaExc100mm, "osmaExc100mm"),
              osmaExc150mm: getNum(columnMap.osmaExc150mm, "osmaExc150mm"),
              // OSMA STORM
              osmaStorm32mmBend: getNum(
                columnMap.osmaStorm32mmBend,
                "osmaStorm32mmBend",
              ),
              osmaStorPPIC: getNum(columnMap.osmaStorPPIC, "osmaStorPPIC"),
              osmaStorm40mmBend: getNum(
                columnMap.osmaStorm40mmBend,
                "osmaStorm40mmBend",
              ),
              osmaStorm50mmBend: getNum(
                columnMap.osmaStorm50mmBend,
                "osmaStorm50mmBend",
              ),
              osmaStorm68mmBend: getNum(
                columnMap.osmaStorm68mmBend,
                "osmaStorm68mmBend",
              ),
              osmaStorm100100JunctionTripleSocket: getNum(
                columnMap.osmaStorm100100JunctionTripleSocket,
                "osmaStorm100100JunctionTripleSocket",
              ),
              osmaStorm82mmBend: getNum(
                columnMap.osmaStorm82mmBend,
                "osmaStorm82mmBend",
              ),
              osmaStorm110mmBend: getNum(
                columnMap.osmaStorm110mmBend,
                "osmaStorm110mmBend",
              ),
              osmaStorm32mmPipe: getNum(
                columnMap.osmaStorm32mmPipe,
                "osmaStorm32mmPipe",
              ),
              osmaStorm40mmPipe: getNum(
                columnMap.osmaStorm40mmPipe,
                "osmaStorm40mmPipe",
              ),
              osmaStorm50mmPipe: getNum(
                columnMap.osmaStorm50mmPipe,
                "osmaStorm50mmPipe",
              ),
              osmaStorm68mmPipe: getNum(
                columnMap.osmaStorm68mmPipe,
                "osmaStorm68mmPipe",
              ),
              osmaStorm82mmPipe: getNum(
                columnMap.osmaStorm82mmPipe,
                "osmaStorm82mmPipe",
              ),
              osmaStorm110mmPipe: getNum(
                columnMap.osmaStorm110mmPipe,
                "osmaStorm110mmPipe",
              ),
              // OSMA STORM Excavation fields
              osmaStormExc100mm: getNum(
                columnMap.osmaStormExc100mm,
                "osmaStormExc100mm",
              ),
              osmaStormExc150mm: getNum(
                columnMap.osmaStormExc150mm,
                "osmaStormExc150mm",
              ),
              osmaStormExc225mm: getNum(
                columnMap.osmaStormExc225mm,
                "osmaStormExc225mm",
              ),
              // POLYPIPE FOUL
              polypipe32mmBend: getNum(
                columnMap.polypipe32mmBend,
                "polypipe32mmBend",
              ),
              polypipePPIC: getNum(columnMap.polypipePPIC, "polypipePPIC"),
              polypipe50mmBend: getNum(
                columnMap.polypipe50mmBend,
                "polypipe50mmBend",
              ),
              polypipe68mmBend: getNum(
                columnMap.polypipe68mmBend,
                "polypipe68mmBend",
              ),
              polypipe82mmBend: getNum(
                columnMap.polypipe82mmBend,
                "polypipe82mmBend",
              ),
              polypipe110mmBend: getNum(
                columnMap.polypipe110mmBend,
                "polypipe110mmBend",
              ),
              polypipe32mmPipe: getNum(
                columnMap.polypipe32mmPipe,
                "polypipe32mmPipe",
              ),
              polypipe40mmPipe: getNum(
                columnMap.polypipe40mmPipe,
                "polypipe40mmPipe",
              ),
              polypipe50mmPipe: getNum(
                columnMap.polypipe50mmPipe,
                "polypipe50mmPipe",
              ),
              polypipe68mmPipe: getNum(
                columnMap.polypipe68mmPipe,
                "polypipe68mmPipe",
              ),
              polypipe82mmPipe: getNum(
                columnMap.polypipe82mmPipe,
                "polypipe82mmPipe",
              ),
              polypipe110mmPipe: getNum(
                columnMap.polypipe110mmPipe,
                "polypipe110mmPipe",
              ),
              // POLYPIPE FOUL Excavation fields
              polypipeExc100mm: getNum(
                columnMap.polypipeExc100mm,
                "polypipeExc100mm",
              ),
              polypipeExc150mm: getNum(
                columnMap.polypipeExc150mm,
                "polypipeExc150mm",
              ),
              // POLYPIPE STORM
              polypipeStorm32mmBend: getNum(
                columnMap.polypipeStorm32mmBend,
                "polypipeStorm32mmBend",
              ),
              polypipeStormPPIC: getNum(
                columnMap.polypipeStormPPIC,
                "polypipeStormPPIC",
              ),
              polypipeStorm50mmBend: getNum(
                columnMap.polypipeStorm50mmBend,
                "polypipeStorm50mmBend",
              ),
              polypipeStorm68mmBend: getNum(
                columnMap.polypipeStorm68mmBend,
                "polypipeStorm68mmBend",
              ),
              polypipeStorm100100JunctionTripleSocket: getNum(
                columnMap.polypipeStorm100100JunctionTripleSocket,
                "polypipeStorm100100JunctionTripleSocket",
              ),
              polypipeStorm82mmBend: getNum(
                columnMap.polypipeStorm82mmBend,
                "polypipeStorm82mmBend",
              ),
              polypipeStorm110mmBend: getNum(
                columnMap.polypipeStorm110mmBend,
                "polypipeStorm110mmBend",
              ),
              polypipeStorm32mmPipe: getNum(
                columnMap.polypipeStorm32mmPipe,
                "polypipeStorm32mmPipe",
              ),
              polypipeStorm40mmPipe: getNum(
                columnMap.polypipeStorm40mmPipe,
                "polypipeStorm40mmPipe",
              ),
              polypipeStorm50mmPipe: getNum(
                columnMap.polypipeStorm50mmPipe,
                "polypipeStorm50mmPipe",
              ),
              polypipeStorm68mmPipe: getNum(
                columnMap.polypipeStorm68mmPipe,
                "polypipeStorm68mmPipe",
              ),
              polypipeStorm82mmPipe: getNum(
                columnMap.polypipeStorm82mmPipe,
                "polypipeStorm82mmPipe",
              ),
              polypipeStorm110mmPipe: getNum(
                columnMap.polypipeStorm110mmPipe,
                "polypipeStorm110mmPipe",
              ),
              // POLYPIPE STORM Excavation fields
              polypipeStormExc100mm: getNum(
                columnMap.polypipeStormExc100mm,
                "polypipeStormExc100mm",
              ),
              polypipeStormExc150mm: getNum(
                columnMap.polypipeStormExc150mm,
                "polypipeStormExc150mm",
              ),
              polypipeStormExc225mm: getNum(
                columnMap.polypipeStormExc225mm,
                "polypipeStormExc225mm",
              ),
            };

            importedRows.push(drainageRow);
          }

          if (importedRows.length === 0) {
            alert("No data rows found to import. Please check the Excel file.");
            return;
          }

          // Persist extracted headers
          if (Object.keys(extractedHeaders).length > 0) {
            const mergedHeaders = { ...drainageColumnHeaders, ...extractedHeaders };
            const mergedDimValues = { ...drainageDimensionValues, ...extractedDimValues };
            setDrainageColumnHeaders(mergedHeaders);
            setDrainageDimensionValues(mergedDimValues);
          }

          // Update the standard data
          setStandardData(importedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            drainageTableStandard: importedRows,
            ...(Object.keys(extractedHeaders).length > 0 && {
              drainageColumnHeaders: { ...drainageColumnHeaders, ...extractedHeaders },
              drainageDimensionValues: { ...drainageDimensionValues, ...extractedDimValues },
            }),
          });
          alert(
            `Successfully imported ${importedRows.length} rows into Standard view`,
          );
        } catch (error: any) {
          console.error("Import error:", error);
          alert(`Import failed: ${error?.message || "Unknown error"}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error: any) {
      console.error("Import error:", error);
      alert(`Import failed: ${error?.message || "Unknown error"}`);
    }
  };

  // Sync drainageDimensionValues from context (e.g. after import)
  useEffect(() => {
    if (tender?.groundworks?.drainageDimensionValues) {
      setDrainageDimensionValues(tender.groundworks.drainageDimensionValues);
    }
  }, [tender?.groundworks?.drainageDimensionValues]);

  // Sync drainagePolypipeConfig from context (e.g. after import)
  useEffect(() => {
    if ((tender?.groundworks as any)?.drainagePolypipeConfig) {
      setDrainagePolypipeConfig((tender.groundworks as any).drainagePolypipeConfig);
    }
  }, [(tender?.groundworks as any)?.drainagePolypipeConfig]);

  // Load Standard View data from context (only once on mount)
  useEffect(() => {
    if (tender?.groundworks?.drainageTableStandard) {
      setStandardData(tender.groundworks.drainageTableStandard);
    } else if (tender?.groundworks?.drainageTable) {
      // Fallback to old drainageTable for backward compatibility
      setStandardData(tender.groundworks.drainageTable);
    } else {
      // Initialize with empty row
      setStandardData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.drainageTableStandard]);

  // Keep drainageTable synced with standard data for backward compatibility
  useEffect(() => {
    if (standardData && standardData.length > 0) {
      updateField("groundworks", {
        ...tender.groundworks,
        drainageTable: standardData,
      });
    }
  }, [standardData]);

  // Detect and set supplier based on existing table data on initial load
  useEffect(() => {
    if (
      !hasDetectedSupplier.current &&
      standardData &&
      standardData.length > 0
    ) {
      const detectedSupplier = detectSupplierFromData(standardData);
      if (detectedSupplier) {
        setStandardSupplier(detectedSupplier);
        hasDetectedSupplier.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standardData]); // Run when data is loaded

  // Handle drainage column headers change from StandardDrainageView
  const handleDrainageColumnHeadersChange = (
    headers: Record<string, string>,
    dimensions: Record<string, string>
  ) => {
    setDrainageColumnHeaders(headers);
    setDrainageDimensionValues(dimensions);
    updateField("groundworks", {
      ...tender.groundworks,
      drainageColumnHeaders: headers,
      drainageDimensionValues: dimensions,
    });
    setTimeout(() => {
      saveTender();
    }, 100);
  };

  const handlePolypipeConfigChange = (values: Record<string, string>) => {
    setDrainagePolypipeConfig(values);
    updateField("groundworks", {
      ...tender.groundworks,
      drainagePolypipeConfig: values,
    });
    setTimeout(() => {
      saveTender();
    }, 100);
  };


  const createEmptyRow = (key?: string): DrainageRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    plots: 0,
    apartments: 0,
    nmePhic: 0,
    yardGully: 0,
    drainageChannel: 0,
    landDrain: 0,
    cellularAtten: 0,
    polypipeFoul: 0,
    polypipeStorm: 0,
    osmaFoul: 0,
    osmaStorm: 0,
    // Additional fields for OSMA-FOUL
    gravel: 0,
    concealedSetSurround: 0,
    tipOnSite: 0,
    osmaNmePpic: 0,
    osma600mmNmePpic: 0,
    // Additional fields for OSMA-STORM
    osmaStormGravel: 0,
    osmaStormConcreteBedSurround: 0,
    osmaStormTipOnsite: 0,
    osmaStormNmePpic: 0,
    osmaStorm600mmNmePpic: 0,
    osmaStormYardGully: 0,
    osmaStormDrainageChannel: 0,
    osmaStormLandDrain: 0,
    // Additional fields for POLYPIPE-FOUL
    polypipeGravel: 0,
    polypipeConcreteBedSurround: 0,
    polypipeTipOnSite: 0,
    polypipeNmePpic: 0,
    polypipe600mmNmePpic: 0,
    // Additional fields for POLYPIPE-STORM
    polypipeStormGravel: 0,
    polypipeStormConcreteBedSurround: 0,
    polypipeStormTipOnSite: 0,
    polypipeStormNmePpic: 0,
    polypipeStorm600mmNmePpic: 0,
    polypipeStormYardGully: 0,
    polypipeStormDrainageChannel: 0,
    polypipeStormLandDrain: 0,
    // SW & S detailed fields for OSMA FOUL
    osma32mmBend: 0,
    osmaPPIC: 0,
    osma40mmBend: 0,
    osma50mmBend: 0,
    osma68mmBend: 0,
    osma82mmBend: 0,
    osma110mmBend: 0,
    osma32mmPipe: 0,
    osma40mmPipe: 0,
    osma50mmPipe: 0,
    osma68mmPipe: 0,
    osma82mmPipe: 0,
    osma110mmPipe: 0,
    osmaExc100mm: 0,
    osmaExc150mm: 0,
    // SW & S detailed fields for OSMA STORM
    osmaStorm32mmBend: 0,
    osmaStorPPIC: 0,
    osmaStorm40mmBend: 0,
    osmaStorm50mmBend: 0,
    osmaStorm68mmBend: 0,
    osmaStorm100100JunctionTripleSocket: 0,
    osmaStorm82mmBend: 0,
    osmaStorm110mmBend: 0,
    osmaStorm32mmPipe: 0,
    osmaStorm40mmPipe: 0,
    osmaStorm50mmPipe: 0,
    osmaStorm68mmPipe: 0,
    osmaStorm82mmPipe: 0,
    osmaStorm110mmPipe: 0,
    osmaStormExc100mm: 0,
    osmaStormExc150mm: 0,
    osmaStormExc225mm: 0,
    // SW & S detailed fields for POLYPIPE FOUL
    polypipe32mmBend: 0,
    polypipePPIC: 0,
    polypipe40mmBend: 0,
    polypipe50mmBend: 0,
    polypipe68mmBend: 0,
    polypipe82mmBend: 0,
    polypipe110mmBend: 0,
    polypipe32mmPipe: 0,
    polypipe40mmPipe: 0,
    polypipe50mmPipe: 0,
    polypipe68mmPipe: 0,
    polypipe82mmPipe: 0,
    polypipe110mmPipe: 0,
    polypipeExc100mm: 0,
    polypipeExc150mm: 0,
    polypipeNmePpic: 0,
    polypipe600mmNmePpic: 0,
    // SW & S detailed fields for POLYPIPE STORM
    polypipeStorm32mmBend: 0,
    polypipeStormPPIC: 0,
    polypipeStorm40mmBend: 0,
    polypipeStorm50mmBend: 0,
    polypipeStorm68mmBend: 0,
    polypipeStorm100100JunctionTripleSocket: 0,
    polypipeStorm82mmBend: 0,
    polypipeStorm110mmBend: 0,
    polypipeStorm32mmPipe: 0,
    polypipeStorm40mmPipe: 0,
    polypipeStorm50mmPipe: 0,
    polypipeStorm68mmPipe: 0,
    polypipeStorm82mmPipe: 0,
    polypipeStorm110mmPipe: 0,
    polypipeStormExc100mm: 0,
    polypipeStormExc150mm: 0,
    polypipeStormExc225mm: 0,
    polypipeStormNmePpic: 0,
    polypipeStorm600mmNmePpic: 0,
    polypipeStormYardGully: 0,
    polypipeStormDrainageChannel: 0,
    polypipeStormLandDrain: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof DrainageRow,
    value: string | number,
  ) => {
    const currentRow = data.find((row) => row.key === key);
    const oldValue = currentRow ? currentRow[field] : undefined;
    // trackFieldChange(field as string, oldValue, value, key);

    const newData = data.map((row) => {
      if (row.key === key) {
        // Convert string to number for numeric fields
        let processedValue = value;
        if (
          typeof value === "string" &&
          field !== "plotNo" &&
          field !== "housetype" &&
          field !== "key"
        ) {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue)
            ? 0
            : Math.round(numValue * 1000) / 1000;
        }
        return { ...row, [field]: processedValue };
      }
      return row;
    });

    setData(newData);
    // Update tender context
    updateField("groundworks", {
      ...tender.groundworks,
      drainageTableStandard: newData,
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    // Update tender context
    updateField("groundworks", {
      ...tender.groundworks,
      drainageTableStandard: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = (keys: React.Key[]) => {
    setKeysToDelete(keys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Update tender context
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Drainage Bill Summary — see CutFills.tsx's identical
      // fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        drainageTableStandard: newData,
        ...(newData.length === 0 && { drainageExtraItems: {} }),
      });
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(
        tender,
        "Save and continue from Drainage",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/plot-drives"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(
        tender,
        "Save and exit from Drainage",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  return (
    <MainLayout showFooter={false}>
      <StandardDrainageView
        data={data}
        selectedSupplier={standardSupplier}
        onSupplierChange={handleSupplierChange}
        isViewMode={tender.isViewMode}
        onInputChange={handleInputChange}
        onAddRow={addRow}
        onDeleteRow={deleteRow}
        onDeleteRows={deleteSelectedRows}
        onExport={handleExportExcel}
        onImport={handleImportExcel}
        onDownloadTemplate={handleDownloadTemplate}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
        onSave={handleSave}
        onSaveAndContinue={handleSaveAndContinue}
        onSaveAndExit={handleSaveAndExit}
        columnHeaders={drainageColumnHeaders}
        dimensionValues={drainageDimensionValues}
        onColumnHeadersChange={handleDrainageColumnHeadersChange}
        polypipeDimensionValues={drainagePolypipeConfig}
        onPolypipeConfigChange={handlePolypipeConfigChange}
      />

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={keysToDelete.length > 1 ? `Are you sure you want to delete ${keysToDelete.length} records?` : undefined}
      />
    </MainLayout>
  );
};

export default Drainage;
